import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { myMembershipsQuery } from "../api/queries";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { useT } from "../i18n/locale";
import { STATUS_REQUIRED_AFTER_SECONDS } from "../leads/card/OutcomeSteps";
import { useOptionalTab } from "../session/access";
import { tabKey, type TabContext } from "../session/tab-context";
import { isCallLive, numberDiagnosisOf, useCallSession, type CallSession } from "./call-session";
import { callErrorText, needsReload } from "./call-texts";
import { reloadPage } from "./reload-page";
import "./call-bar.css";

// La barra della chiamata in alto nel contenuto (piano M4, Task 13, D10): fuori dalla scheda del Lead chiamato dice «In
// chiamata con …» con «Torna alla scheda» e «Chiudi la chiamata»; chiusa la chiamata, finché la postazione non si chiude, il
// pulsante che torna alla scheda dice che cosa manca davvero (richiesta del product owner del 5 ottobre 2026, `missingText`):
// l'esito («Manca l'esito: torna alla scheda»), l'orario dopo «Richiamare», lo status dopo almeno due minuti con «No Type»,
// oppure, fatto tutto, soltanto chiudere la chiamata. Una chiamata che non è partita (un errore del fornitore mentre si era su
// un'altra pagina) dice perché, con «Torna alla scheda» (non per una chiamata che il server non trova più) e «Chiudi
// l'avviso», che lo toglie senza tornare alla scheda; se è il telefono del browser che non si è caricato (la pagina è di prima
// di un aggiornamento), prima di tutti c'è «Ricarica la pagina», l'unica cosa che lo risolve. Sulla scheda del Lead chiamato
// non c'è: lì c'è la postazione. «Torna alla scheda» porta alla scheda soltanto se la scheda del browser lavora ancora
// nell'azienda della chiamata; in un'altra azienda la barra dice di quale azienda è la chiamata, invece di un pulsante che non
// porta da nessuna parte (minore di R2 per R3).

export function CallBar() {
  const t = useT();
  const { session, pendingSteps, hangUp, dismiss } = useCallSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const tab = useOptionalTab();
  const hintId = useId();
  const target = session.phase === "idle" ? null : session.target;
  const sameCompany = target !== null && tab !== null && tabKey(tab) === tabKey(target.tab);
  if (session.phase === "idle" || target === null) return null;
  if (pathname === `/lead/${encodeURIComponent(target.leadId)}`) return null;
  const back = () => void navigate({ to: "/lead/$leadId", params: { leadId: target.leadId } });
  // Una chiamata che il server non trova più (il Lead eliminato per sempre): non c'è una scheda a cui tornare, nemmeno da
  // un'altra azienda.
  const gone = session.phase === "error" && !(session.error instanceof ApiRequestError) && session.error.kind === "call_gone";
  const hint = sameCompany || gone ? null : <OtherCompanyHint id={hintId} tab={target.tab} />;

  if (session.phase === "error") {
    return (
      <section className="g-call-bar" aria-label={t("callDesk.barLabel")} data-phase={session.phase}>
        <p className="g-call-bar__text" role="alert">
          <Icon name="alert" />
          <span>{callErrorText(t, session.error)}</span>
        </p>
        <span className="g-call-bar__actions">
          {needsReload(session.error) ? (
            <Button variant="primary" onClick={reloadPage}>
              {t("callDesk.reload")}
            </Button>
          ) : null}
          {sameCompany && !gone ? <Button onClick={back}>{t("callDesk.barBack")}</Button> : null}
          <Button variant="ghost" onClick={dismiss}>
            {t("callDesk.dismiss")}
          </Button>
        </span>
        {hint}
      </section>
    );
  }

  if (session.phase === "ending") {
    return <section className="g-call-bar" aria-label={t("callDesk.barLabel")} data-phase="ending">
      <p className="g-call-bar__text" role="status">{t("callDesk.ending")}</p>
      {sameCompany ? <Button onClick={back}>{t("callDesk.barBack")}</Button> : null}{hint}
    </section>;
  }

  return (
    <section className="g-call-bar" aria-label={t("callDesk.barLabel")} data-phase={session.phase}>
      {isCallLive(session) ? (
        <>
          <p className="g-call-bar__text">
            <Icon name="phone" />
            <span>{t("callDesk.barLive", { name: session.leadName })}</span>
          </p>
          <span className="g-call-bar__actions">
            {sameCompany ? <Button onClick={back}>{t("callDesk.barBack")}</Button> : null}
            <Button onClick={hangUp}>
              <Icon name="hangup" />
              {t("callDesk.hangUp")}
            </Button>
          </span>
        </>
      ) : (
        <Button variant="primary" disabled={!sameCompany} aria-describedby={sameCompany ? undefined : hintId} onClick={back}>
          {missingText(t, session, pendingSteps)}
        </Button>
      )}
      {hint}
    </section>
  );
}

/**
 * Il pulsante della barra a chiamata chiusa: che cosa manca davvero per chiudere la postazione. L'esito finché il server non
 * lo conferma; poi il primo passo obbligatorio che la postazione ha detto ancora da fare (`pendingSteps`, nel suo ordine:
 * l'orario dopo «Richiamare», poi lo status); fatto tutto, soltanto «Chiudi la chiamata» sulla scheda. Un passo obbligatorio
 * che qui non ha parole sue (uno nuovo) si dice con quelle generali.
 */
function missingText(t: ReturnType<typeof useT>, session: Extract<CallSession, { phase: "outcome" }>, pendingSteps: readonly string[]): string {
  if (numberDiagnosisOf(session.call) && session.save.state !== "saved") return t("callDesk.invalidNumberLabel");
  if (session.save.state !== "saved") return t("callDesk.barMissing");
  const [step] = pendingSteps;
  if (step === undefined) return t("callDesk.barSaved");
  if (step === "callback") return t("callDesk.barMissingCallback");
  if (step === "status") return t("callDesk.barMissingStatus", { minutes: STATUS_REQUIRED_AFTER_SECONDS / 60 });
  return t("callDesk.barMissingStep");
}

/**
 * Di quale azienda è una chiamata nata in un'altra azienda della scheda, con il nome che le appartenenze della persona le danno
 * (il cambio di azienda le ha già lette); senza il nome, «un'altra azienda».
 */
function OtherCompanyHint({ id, tab }: { id: string; tab: TabContext }) {
  const t = useT();
  const memberships = useQuery(myMembershipsQuery);
  const company = tab.kind === "membership" ? memberships.data?.find((membership) => membership.membershipId === tab.membershipId)?.organizationName : undefined;
  return (
    <p className="g-call-bar__hint" id={id}>
      {company === undefined ? t("callDesk.barOtherCompanyUnknown") : t("callDesk.barOtherCompany", { company })}
    </p>
  );
}
